import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { WizardSplit, WizardCard, WizardHeading, WizardProse, WizardButtons } from '@/components/WizardParts'
import { Button } from '@/components/Button'
import { Strip } from '@/components/Strip'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { getBootConfiguration } from '@/data/bootConfig'
import { OB_WZ_3_COPY } from '@/copy/onboarding'
import { FormError, RateLimitedNotice, UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import type { components } from '@/api/generated/schema'

type Workspace = components['schemas']['Workspace']
type Region = components['schemas']['Region']

/** `ob-wz-3` — the region choice, and the pane that fires `workspaceConnect` (LLD-API-068).
 * "Chosen once, here, and immutable thereafter" — the confirmation checkbox is required before
 * the call can fire. **The region list and each one's rendered name come from
 * `platformGetConfiguration`'s own catalogue** (`getBootConfiguration()`, fetched at boot and
 * held, `main.tsx` never mounts a route before it resolves) — `LLD-SPA-004`/§31.7 forbid a
 * region code as a literal in the built bundle, and this screen is what makes that concrete: it
 * is the one place in the app that chooses one. Found by the build's own env-literal gate the
 * first time this layer was actually wired into a reachable route (W153) — the literal predates
 * this pass but was invisible until then, tree-shaken out of every bundle nothing imported it into.
 *
 * **`data-cmp="pane"` added W166** — the unit root carried `data-scr` but never its own `pane`. */
export function ObWz3({
  orgId,
  credential,
  workspaceName,
  onBack,
  onConnected,
}: {
  orgId: string
  credential: string
  workspaceName: string
  onBack: () => void
  onConnected: (workspace: Workspace) => void
}) {
  const regions = getBootConfiguration()!.regions
  const [region, setRegion] = useState<Region>(regions.find((r) => r.home)?.region ?? regions[0]!.region)
  const [confirmed, setConfirmed] = useState(true)

  const connect = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.POST('/orgs/{org_id}/workspaces', {
        params: { path: { org_id: orgId } },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
        body: { workspace_name: workspaceName, credential, region },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
    onSuccess: (workspace) => {
      if (workspace) onConnected(workspace)
    },
  })

  const problem = connect.error instanceof ApiProblemError ? connect.error.problem : undefined

  return (
    <div data-scr={unitId('ob-wz-3')} data-cmp="pane" className="p-10">
      <WizardSplit>
        <div>
          <WizardHeading>{OB_WZ_3_COPY.heading}</WizardHeading>
          <WizardProse>{OB_WZ_3_COPY.proseOneTemplate(workspaceName)}</WizardProse>
          <WizardProse>{OB_WZ_3_COPY.proseTwo}</WizardProse>
          <WizardProse>{OB_WZ_3_COPY.proseThree}</WizardProse>
        </div>
        <WizardCard>
          <h4 className="m-0 mb-7 text-body-lg font-semibold">{OB_WZ_3_COPY.cardHeading}</h4>
          <div className="flex flex-col gap-5 mb-7">
            {regions.map((r) => (
              <label key={r.region} className="flex items-center gap-3">
                <input type="radio" name="ob-region" checked={region === r.region} onChange={() => setRegion(r.region)} />
                <b className="text-body-lg">{r.display_name}</b>
                <span className="text-small text-muted">— {r.region}</span>
              </label>
            ))}
          </div>
          <div className="mb-7">
            <Strip>{OB_WZ_3_COPY.permanentWarning}</Strip>
          </div>
          <Note>{OB_WZ_3_COPY.residencyNote}</Note>
          <label className="flex gap-3 items-start my-7">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            <span className="text-small text-tx2">{OB_WZ_3_COPY.confirmTemplate(workspaceName)}</span>
          </label>

          {problem && <ObWz3Refusal problem={problem} />}

          <WizardButtons>
            <Button data-act={actionId('nav.ob-wz-2')} ghost onClick={onBack}>
              {OB_WZ_3_COPY.backButton}
            </Button>
            <Button data-act={actionId('workspace.connect')} disabled={!confirmed || connect.isPending} onClick={() => connect.mutate()}>
              {connect.isPending ? 'Connecting…' : OB_WZ_3_COPY.connectButton}
            </Button>
          </WizardButtons>
        </WizardCard>
      </WizardSplit>
    </div>
  )
}

function ObWz3Refusal({ problem }: { problem: Problem }) {
  switch (problem.refusal_kind) {
    case 'VALIDATION_FAILED':
      return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
    case 'RATE_LIMITED':
      return <RateLimitedNotice problem={problem} />
    default:
      return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
  }
}
